import { useEffect, useRef, useState } from "react";
import { ArrowButton } from "../../General/ArrowButton/ArrowButton";
import { Check } from "lucide-react";
import { BtnLoadIndication } from "../../Loader/BtnLoadIndication";

interface ListItem {
    id: string;
    value: string;
}

interface Props {
    currentItem: ListItem;
    setCurrentItem: (brand: ListItem) => void;
    list: ListItem[];
    disabled?: boolean;
    isSmall?: boolean;
    isLoading?: boolean;
}

/**
 * Кнопка с выпадающим списком
 * @param currentItem - текущий выбранный элемент
 * @param setCurrentItem - функция для установки выбранного элемента
 * @param list - список элементов
 * @param disabled - флаг для отключения компонента
 * @param isSmall - флаг для уменьшения размера компонента
 * @returns 
 */
export const OpeneningList: React.FC<Props> = ({
    currentItem,
    setCurrentItem,
    list,
    disabled = false,
    isSmall = false,
    isLoading = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(isOpen);
    const containerRef = useRef<HTMLDivElement>(null);
    const ulRef = useRef<HTMLUListElement>(null);
    const currentElementRef = useRef<HTMLDivElement>(null);
    const inputLineRef = useRef('');

    /** Обновление состояния открытия списка */
    useEffect(() => {
        //Задержка установки состояния, чтобы избежать моментального закрытия
        setTimeout(() => isOpenRef.current = isOpen)
    }, [isOpen]);

    /** Перехват клика вне компонента */
    useEffect(() => {
        /** Перехват клика вне компонента */
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpenRef.current && containerRef.current && event.target !== containerRef.current) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        let lineTimeout: NodeJS.Timeout;
        let scrollTimeout: NodeJS.Timeout;

        /** Перехват ввода с клавиатуры */
        const handleKeyDown = (event: KeyboardEvent) => {
            inputLineRef.current += event.key;

            const finded = list.find(item => {
                return item.value.toLowerCase().startsWith(inputLineRef.current.toLowerCase());
            });

            if (finded) {
                const element = document.getElementById(finded.id);
                if (element) {
                    scrollTimeout = setTimeout(() => {
                        scroll(element);
                    }, 300);
                }
            }

            //Сброс строки ввода через 1 секунду
            lineTimeout = setTimeout(() => {
                inputLineRef.current = '';
            }, 1000);
        };

        if (isOpen) {
            containerRef.current?.addEventListener('keydown', handleKeyDown);

            return () => {
                containerRef.current?.removeEventListener('keydown', handleKeyDown);
                clearTimeout(lineTimeout);
                clearTimeout(scrollTimeout);
            };
        }
    }, [isOpen]);

    /** Скролл к элементу */
    const scroll = (element: HTMLElement) => {
        const ELEMENT_OFFSET = 100;

        const containerRect = ulRef.current?.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const offset = elementRect.top - (containerRect?.top ?? 0) - ELEMENT_OFFSET;
        const scrollTop = (ulRef.current?.scrollTop ?? 0) + offset;

        ulRef.current?.scrollTo({ top: scrollTop, behavior: 'smooth' });
    };

    /** Открытие списка */
    const handleOpen = () => {
        if (!isOpen) {
            setIsOpen(!isOpen);

            //Скролл к ранее выбранному элементу
            if (currentElementRef.current) {
                scroll(currentElementRef.current);
            }
        }
    };

    /** Перехват клика на элемент списка */
    const handleSelect = (item: ListItem) => {
        setCurrentItem(item);
        setIsOpen(false);
    };

    //Ширина кнопки
    const width = isSmall ? 'sm:w-32' : 'sm:w-48';

    return (
        <div className={`relative w-full ${width}`} ref={containerRef}>
            <div className={`h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 [&>span]:line-clamp-1 w-full ${width}`}>
                <button type="button"
                    className="w-full flex items-center justify-between disabled:cursor-not-allowed disabled:opacity-50"
                    role="combobox"
                    aria-expanded="false"
                    disabled={disabled}
                    onClick={handleOpen}>
                    <span className="overflow-hidden whitespace-nowrap"
                        style={{ pointerEvents: 'none', textOverflow: 'ellipsis' }}>
                        {currentItem.value}
                    </span>
                    <span><ArrowButton state={isOpen ? 'up' : 'down'} opacity={50} size={4} /></span>
                </button>
                <BtnLoadIndication isLoading={isLoading} />
            </div>
            {!disabled && <ul ref={ulRef}
                className={`absolute ${isOpen ? 'max-h-60 py-2 border' : 'max-h-0'} transition-all overflow-y-auto scroll-window z-40 rounded-md border-input bg-background px-3 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 w-full mt-1 ${width}`}>
                {list.map((item) => (
                    <li className="w-full">
                        <button type="button"
                            className="w-full bs-bb flex relative cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                            key={`brand-${item.id}`}
                            id={item.id}
                            onClick={() => handleSelect(item)}>
                            {currentItem.value === item.value &&
                                <div className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"
                                    ref={currentElementRef}>
                                    <Check className="h-4 w-4" />
                                </div>
                            }
                            <span>{item.value}</span>
                        </button>
                    </li>
                ))}
            </ul>}
        </div>
    );
};